import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  useColorModeValue,
  Link,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { authService } from "../../api/auth";
import { useAuth } from "../../hooks/useAuth";

const ForgotPasswordForm = () => {
  const { user, isAuthenticated } = useAuth();
  const authed = isAuthenticated();
  const authedEmail = authed ? (user?.email || "") : "";
  const [email, setEmail] = useState(authed ? authedEmail : "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const formBg = useColorModeValue("var(--color-card-bg)", "var(--color-card-bg)");
  const borderColor = useColorModeValue("var(--color-border)", "var(--color-border)");
  const subtitleColor = useColorModeValue("var(--color-text-muted)", "var(--color-text-muted)");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    const targetEmail = authed ? authedEmail : email;
    try {
      await authService.requestPasswordReset(targetEmail);
      try { localStorage.setItem('reset_email', email); } catch {}
      navigate('/reset-password', { state: { email: targetEmail, notice: 'reset_sent' } });
    } catch (err: any) {
      setError("Failed to send reset code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Box
      maxW="md"
      w="full"
      bg={formBg}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      p={8}
      boxShadow="lg"
    >
      <VStack spacing={6} as="form" onSubmit={handleSubmit}>
        <VStack spacing={2}>
          <Heading size="lg" fontWeight="bold">
            Forgot Password?
          </Heading>
          {authed ? (
            <Text fontSize="sm" color={subtitleColor} textAlign="center">
              We will send a 6-digit reset code to your account email: <strong>{authedEmail}</strong>
            </Text>
          ) : (
            <Text fontSize="sm" color={subtitleColor} textAlign="center">
              Enter your email address and we’ll send you a 6-digit reset code.
            </Text>
          )}
        </VStack>

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {!authed && (
          <FormControl isRequired>
            <FormLabel>Email Address</FormLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              autoComplete="email"
              autoFocus
            />
          </FormControl>
        )}

        <Button
          type="submit"
          colorScheme="blue"
          size="lg"
          w="full"
          isLoading={isLoading}
          loadingText="Sending..."
          isDisabled={(authed ? false : !email) || isLoading}
        >
          Send Code
        </Button>

        <Link
          as={RouterLink}
          to="/login"
          fontSize="sm"
          color="blue.500"
          display="flex"
          alignItems="center"
          gap={1}
        >
          <ArrowBackIcon />
          Back to Login
        </Link>
      </VStack>
    </Box>
  );
};

export default ForgotPasswordForm;
